import React, { useEffect, useState } from 'react';
import _isEmpty from 'lodash.isempty';
import {
  FormModal,
  Icon,
  Text,
  Row,
  Divider,
  Button,
  getBucket
} from '@shootsta/common-react';

import '../styles/_flex-purchase-payment-success-modal.scss';

const FlexPurchasePaymentSuccessModal = ({
  visible,
  contractItemBalance,
  purchasedCredit,
  organisationId,
  orderId,
  onClose,
  onViewInvoice,
  promoBonusCredits = 0,
  financeLedgerEnabled = false,
  getFinanceBalance
}: {
  visible: boolean;
  purchasedCredit: number;
  promoBonusCredits?: number;
  financeLedgerEnabled?: boolean;
  getFinanceBalance?: (
    args: Record<string, never>
  ) => Promise<{ data: any; error: any }>;
  contractItemBalance: any;
  organisationId: string;
  orderId: string;
  onClose: (args: boolean) => void;
  onViewInvoice: (videoDetails: any) => void;
}) => {
  function calculatePrevCreditBalance() {
    if (_isEmpty(contractItemBalance)) return;

    const allCreditTypeBalances = getBucket(contractItemBalance);

    if (_isEmpty(allCreditTypeBalances)) return 0;

    const totalCreditsBalance = allCreditTypeBalances
      .map((item: any) => item.credit)
      .reduce((prev: number, next: number) => prev + next);

    const totalTopupCreditsBalance = allCreditTypeBalances
      .map((item: any) => item.topUpCredit)
      .reduce((prev: number, next: number) => prev + next);

    return totalCreditsBalance + totalTopupCreditsBalance;
  }

  const bonus = Number(promoBonusCredits) || 0;
  const creditsAdded = Number(purchasedCredit) + bonus;

  // With the finance ledger on, the contract balance is not the workspace's
  // balance. The ledger already holds this top-up when the modal opens, so
  // the ledger total is the current balance and the previous one is that
  // minus what was just added.
  const useLedger = financeLedgerEnabled && !!getFinanceBalance;
  const [ledgerBalance, setLedgerBalance] = useState<number | null>(null);
  const [ledgerState, setLedgerState] = useState<'loading' | 'ready' | 'error'>(
    'loading'
  );

  useEffect(() => {
    if (!useLedger || !getFinanceBalance) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await getFinanceBalance({});
        if (cancelled) return;
        const total = data?.getFinanceBalance?.totalRemaining;
        if (error || typeof total !== 'number') {
          setLedgerState('error');
          return;
        }
        setLedgerBalance(total);
        setLedgerState('ready');
      } catch {
        if (!cancelled) setLedgerState('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [useLedger, getFinanceBalance]);

  const contractPrevBalance = useLedger ? 0 : calculatePrevCreditBalance();

  const balanceText = (value: number | undefined | null) => {
    if (useLedger && ledgerState === 'loading') return 'Loading…';
    if (useLedger && ledgerState === 'error') return 'Unavailable';
    return `${value} credits`;
  };

  const prevCreditBalance = useLedger
    ? ledgerBalance !== null
      ? ledgerBalance - creditsAdded
      : null
    : contractPrevBalance;

  const totalCredits = useLedger
    ? ledgerBalance
    : Number(contractPrevBalance) + creditsAdded;

  const onCloseHandler = () => onClose(false);

  const openInvoice = () => {
    const videoDetails = {
      videoId: '',
      organisationId,
      orderId,
      isUrl: true
    };
    onViewInvoice(videoDetails);
  };

  return (
    <FormModal
      visible={visible}
      showExtraButtons
      onSave={onCloseHandler}
      onClose={onCloseHandler}
      showCancelBtn={false}
      saveText="Close"
      footerExtraButtons={
        <Button onClick={openInvoice} type="TERTIARY">
          View invoice
        </Button>
      }
    >
      <div className="flex-purchase-payment-success-modal">
        <div className="flex-purchase-payment-success-modal__heading">
          <Icon name="checkedFilled" color="monkey" size="40" />
          <Text
            className="flex-purchase-payment-success-modal__heading__title"
            heading2
          >
            Credit top-up successful!
          </Text>
          <Text className="flex-purchase-payment-success-modal__heading__subtitle">
            Your payment was completed successfully! Feel free to contact us if
            you have any questions.
          </Text>
        </div>

        <div className="flex-purchase-payment-success-modal__content">
          <Row nogutter justify="between">
            <Text>Previous Balance</Text>
            <Text>{balanceText(prevCreditBalance)}</Text>
          </Row>
          <Row nogutter justify="between">
            <Text>Top Up</Text>
            <Text>+ {purchasedCredit} credits</Text>
          </Row>
          {bonus > 0 && (
            <Row nogutter justify="between">
              <Text>Promo bonus</Text>
              <Text>+ {bonus} free credits</Text>
            </Row>
          )}
          <Divider />
          <Row nogutter justify="between">
            <Text highlighted>Current Balance</Text>
            <Text highlighted>{balanceText(totalCredits)}</Text>
          </Row>
        </div>
      </div>
    </FormModal>
  );
};

export default FlexPurchasePaymentSuccessModal;
